import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { doc, onSnapshot } from 'firebase/firestore'
import type { DashboardDoc } from '@racedash/shared'
import { db } from '../lib/firebase'
import { relativeTime } from '../lib/time'
import { forgetDashboard, rememberDashboard } from '../lib/lastDashboard'
import { useShell } from '../AppShell'
import SharePanel from '../sharing/SharePanel'
import { setVisibility } from '../dashboards/api'
import { can } from '@racedash/shared'
import LiveDashboard from '../dashboard/LiveDashboard'

/**
 * A dashboard: the live grid, plus who can see it.
 *
 * The session and the car are chosen here rather than stored, because both are
 * parameters of viewing. See docs/timing-pipeline.md §13.
 */
export default function DashboardView() {
  const { dashboardId } = useParams()
  const { user, orgs } = useShell()
  const [dashboard, setDashboard] = useState<DashboardDoc | null | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!dashboardId) return
    return onSnapshot(
      doc(db, 'dashboards', dashboardId),
      (snap) => {
        setDashboard(snap.exists() ? (snap.data() as DashboardDoc) : null)
        // Where the app opens next time. Remembered on success and dropped on
        // failure, so a deleted dashboard cannot leave the front door pointing
        // at a dead end for good.
        if (snap.exists()) rememberDashboard(dashboardId)
        else forgetDashboard(dashboardId)
      },
      (e) => {
        setError(e.message)
        forgetDashboard(dashboardId)
      },
    )
  }, [dashboardId])

  if (error) {
    return (
      <div className="panel-inline">
        <h1 className="page-title">No access</h1>
        <p className="muted">
          This dashboard doesn't exist, or you don't have access to it. <Link to="/">Back</Link>
        </p>
      </div>
    )
  }

  if (dashboard === undefined) return <p className="muted">Loading…</p>

  if (dashboard === null) {
    return (
      <div className="panel-inline">
        <h1 className="page-title">Not found</h1>
        <p className="muted">
          No dashboard with that id. <Link to="/">Back</Link>
        </p>
      </div>
    )
  }

  const org = orgs.find((o) => o.id === dashboard.orgId)
  const role = org?.role ?? null
  const isPrivate = dashboard.visibility === 'private'
  const isPersonalOrg = org?.kind === 'personal'
  const orgLabel = isPersonalOrg ? 'your personal organisation' : (org?.name ?? 'this organisation')
  const maySetVisibility = can(role, 'dashboard.setVisibility')

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">{dashboard.name}</h1>
          <p className="muted small">edited {relativeTime(dashboard.updatedAt)}</p>
        </div>
        <Link className="btn btn-quiet" to={`/o/${dashboard.orgId}`}>
          Back
        </Link>
      </div>

      <LiveDashboard
        dashboardId={dashboardId!}
        orgId={dashboard.orgId}
        stored={{
          screens: dashboard.screens,
          layout: dashboard.layout,
          defaultCar: dashboard.defaultCar,
        }}
        canEdit={can(role, 'dashboard.edit')}
      />

      {/* A ladder, and the page says so: only me, then the organisation, then
          the organisation plus link holders. */}
      <section className="share">
        <div className="section-head">
          <h2 className="section-title">Who can see this</h2>
          {maySetVisibility && (
            <button
              className="btn btn-sm"
              onClick={() =>
                void setVisibility(dashboardId!, isPrivate ? 'organisation' : 'private')
              }
            >
              {isPrivate ? 'Share with organisation' : 'Make it only me'}
            </button>
          )}
        </div>
        <p className="muted small">
          {isPrivate ? (
            <>
              <strong>Only me.</strong> Nobody else in this organisation can see it, and it
              cannot have a public link while that is true. Organisation owners can still
              recover it if you leave.
            </>
          ) : (
            <>
              <strong>Everyone in {orgLabel}.</strong> Anyone who is a member can see it.
              {isPersonalOrg && ' You are the only member, so for now that is just you.'}
            </>
          )}
        </p>
      </section>

      <SharePanel
        dashboardId={dashboardId!}
        orgId={dashboard.orgId}
        uid={user.uid}
        role={role}
        hasActiveLink={dashboard.hasActiveLink === true}
        isPrivate={isPrivate}
      />


    </>
  )
}
